import {
  EmployerOutreachAction,
  EmployerOutreachSource,
  log as logEmployerOutreachInteraction,
  AvroOptional,
} from "@indeed/signals-schema/com/indeed/signals/schema/viewjob/EmployerOutreachInteractionNew";

import type { OutreachSource } from "../hooks/useEmployerOutreachSource";
import { OutreachType } from "../hooks/useEmployerOutreachUIConfig";

/**
 * Lookup table for mapping outreach source to signal schema enum
 */
const OUTREACH_SOURCE_MAP: Record<string, EmployerOutreachSource> = {
  AUTOSOURCER: EmployerOutreachSource.AUTOSOURCER,
  SMART_SOURCING: EmployerOutreachSource.SMART_SOURCING,
  PREMIUM_MATCH_CANDIDATES: EmployerOutreachSource.PREMIUM_MATCH_CANDIDATES,
};

/** Logged when the entry point is absent or unrecognized. */
const UNKNOWN_ENTRY_POINT = "UNKNOWN";

/**
 * Lookup table for mapping the ViewJob entry point (the `from` query param) to
 * the value logged on the signal. `entryPoint` is a free-form string on the
 * schema, so these normalized names are the analytics contract.
 */
const ENTRY_POINT_MAP: Readonly<Record<string, string>> = {
  "hp.employerOutreach": "HOMEPAGE",
  "myjobs.employerOutreach": "MYJOBS",
};

/**
 * Maps outreach source to the signal schema enum
 */
function mapOutreachSourceToEnum(outreachSource?: OutreachSource): {
  EmployerOutreachSource: EmployerOutreachSource;
} {
  const sourceString = outreachSource ?? "";

  const mappedSource =
    OUTREACH_SOURCE_MAP[sourceString.toUpperCase()] || EmployerOutreachSource.UNKNOWN;

  return { EmployerOutreachSource: mappedSource };
}

/**
 * Maps the ViewJob entry point to its logged name
 */
function mapEntryPoint(entryPoint?: string | null): string {
  return entryPoint ? (ENTRY_POINT_MAP[entryPoint] ?? UNKNOWN_ENTRY_POINT) : UNKNOWN_ENTRY_POINT;
}

/**
 * Logs when a user declines an employer outreach job
 */
export function logEmployerOutreachDecline(params: {
  employerOutreachSource?: OutreachSource;
  accountKey?: string;
  ctk?: string;
  jobKey?: string;
  mobtk?: string;
  isError?: boolean;
  entryPoint?: string | null;
}): void {
  const {
    employerOutreachSource,
    accountKey,
    ctk,
    jobKey,
    mobtk,
    isError = false,
    entryPoint,
  } = params;

  logEmployerOutreachInteraction({
    employerOutreachAction: { EmployerOutreachAction: EmployerOutreachAction.DECLINE },
    employerOutreachSource: mapOutreachSourceToEnum(employerOutreachSource),
    accountKey: AvroOptional.string(accountKey),
    ctk: AvroOptional.string(ctk),
    jobkKey: AvroOptional.string(jobKey),
    mobtk: AvroOptional.string(mobtk),
    isError: AvroOptional.boolean(isError),
    entryPoint: AvroOptional.string(mapEntryPoint(entryPoint)),
  });
}

/**
 * Logs when a user submits a decline reason for an employer outreach job
 */
export function logEmployerOutreachSubmitDeclineReason(params: {
  employerOutreachSource?: OutreachSource;
  accountKey?: string;
  ctk?: string;
  jobKey?: string;
  mobtk?: string;
  isError?: boolean;
  entryPoint?: string | null;
}): void {
  const {
    employerOutreachSource,
    accountKey,
    ctk,
    jobKey,
    mobtk,
    isError = false,
    entryPoint,
  } = params;

  logEmployerOutreachInteraction({
    employerOutreachAction: {
      EmployerOutreachAction: EmployerOutreachAction.SUBMIT_DECLINE_REASON,
    },
    employerOutreachSource: mapOutreachSourceToEnum(employerOutreachSource),
    accountKey: AvroOptional.string(accountKey),
    ctk: AvroOptional.string(ctk),
    jobkKey: AvroOptional.string(jobKey),
    mobtk: AvroOptional.string(mobtk),
    isError: AvroOptional.boolean(isError),
    entryPoint: AvroOptional.string(mapEntryPoint(entryPoint)),
  });
}

/**
 * Logs when a user accepts (applies to) an employer outreach job
 */
export function logEmployerOutreachApply(params: {
  employerOutreachSource?: OutreachSource;
  outreachType?: OutreachType;
  accountKey?: string;
  ctk?: string;
  jobKey?: string;
  mobtk?: string;
  isError?: boolean;
  entryPoint?: string | null;
}): void {
  const {
    employerOutreachSource,
    outreachType,
    accountKey,
    ctk,
    jobKey,
    mobtk,
    isError = false,
    entryPoint,
  } = params;

  // Determine if this is a fast apply based on outreach type
  const isFastApply = outreachType === OutreachType.FAST_APPLY;
  const action = isFastApply ? EmployerOutreachAction.FAST_APPLY : EmployerOutreachAction.APPLY;

  logEmployerOutreachInteraction({
    employerOutreachAction: { EmployerOutreachAction: action },
    employerOutreachSource: mapOutreachSourceToEnum(employerOutreachSource),
    accountKey: AvroOptional.string(accountKey),
    ctk: AvroOptional.string(ctk),
    jobkKey: AvroOptional.string(jobKey),
    mobtk: AvroOptional.string(mobtk),
    isError: AvroOptional.boolean(isError),
    entryPoint: AvroOptional.string(mapEntryPoint(entryPoint)),
  });
}

/**
 * Logs when a user completes an apply for an employer outreach job
 */
export function logEmployerOutreachCompleteApply(params: {
  employerOutreachSource?: OutreachSource;
  accountKey?: string;
  ctk?: string;
  jobKey?: string;
  mobtk?: string;
  isError?: boolean;
  entryPoint?: string | null;
}): void {
  const {
    employerOutreachSource,
    accountKey,
    ctk,
    jobKey,
    mobtk,
    isError = false,
    entryPoint,
  } = params;

  logEmployerOutreachInteraction({
    employerOutreachAction: { EmployerOutreachAction: EmployerOutreachAction.COMPLETE_APPLY },
    employerOutreachSource: mapOutreachSourceToEnum(employerOutreachSource),
    accountKey: AvroOptional.string(accountKey),
    ctk: AvroOptional.string(ctk),
    jobkKey: AvroOptional.string(jobKey),
    mobtk: AvroOptional.string(mobtk),
    isError: AvroOptional.boolean(isError),
    entryPoint: AvroOptional.string(mapEntryPoint(entryPoint)),
  });
}
